import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography,
  Avatar, Divider, IconButton, AppBar, Toolbar, useMediaQuery, Menu, MenuItem, Tooltip, alpha,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { SPECIAL_USER_EMAIL } from '../utils/constants';

import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import MapIcon from '@mui/icons-material/Map';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import KeyIcon from '@mui/icons-material/Key';

import { NotificationBell } from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';

const DRAWER_WIDTH = 264;
const RAIL_WIDTH = 76;

const NavButton = styled(ListItemButton)(({ theme }) => ({
  margin: theme.spacing(0.4, 1.25),
  padding: theme.spacing(1.1, 1.75),
  borderRadius: 10,
  color: theme.palette.text.secondary,
  transition: 'all .2s ease',
  '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 0, marginRight: theme.spacing(1.75) },
  '& .MuiListItemText-primary': { fontWeight: 500, fontSize: '0.9rem' },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
    color: theme.palette.text.primary,
  },
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
  },
}));

const AppShell = () => {
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === '1');
  const [anchorEl, setAnchorEl] = useState(null);

  if (!user) return <Outlet />;

  const isAdmin = user.role === 'admin';
  const isSpecialUser = user.email?.toLowerCase() === SPECIAL_USER_EMAIL;

  const items = [
    { text: t('nav.visits'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('calendar.title'), icon: <CalendarMonthIcon />, path: '/calendar' },
    { text: t('nav.catalog'), icon: <MenuBookIcon />, path: '/catalog' },
    { text: t('profile.title'), icon: <PersonIcon />, path: '/profile' },
    { text: t('doctors.title'), icon: <GroupIcon />, path: '/doctors' },
    { text: t('pharmacies.title', 'Аптеки'), icon: <LocalPharmacyIcon />, path: '/pharmacies' },
    ...(isAdmin
      ? [
          { text: t('nav.sales_analytics'), icon: <BarChartIcon />, path: '/analytics/sales' },
          { text: t('nav.map'), icon: <MapIcon />, path: '/reps-map' },
          { text: t('inventory'), icon: <AssignmentIcon />, path: '/inventory' },
          { text: t('specialities.title', 'Specialities'), icon: <LocalHospitalIcon />, path: '/specialities' },
        ]
      : [{ text: t('nav.location'), icon: <MapIcon />, path: '/send-location' }]),
    ...(isSpecialUser
      ? [{ text: t('nav.register_user', 'Register User'), icon: <HowToRegIcon />, path: '/register' }]
      : []),
  ];

  const current = items.find((i) => i.path === location.pathname);
  const pageTitle = current ? current.text : t('app.title');
  const closeMobile = () => setMobileOpen(false);
  const toggleCollapsed = () => setCollapsed((c) => {
    localStorage.setItem('sidebar_collapsed', c ? '0' : '1');
    return !c;
  });
  // Узкий режим (только иконки) — исключительно на десктопе; мобильный Drawer всегда полный
  const mini = isDesktop && collapsed;

  const sidebar = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: mini ? 0 : 2.5, py: 2.25, justifyContent: mini ? 'center' : 'flex-start' }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', color: '#fff',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
        }}>
          <LocalHospitalIcon fontSize="small" />
        </Box>
        {!mini && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.1 }}>Lenus</Typography>
            <Typography variant="caption" color="text.secondary">Med CRM</Typography>
          </Box>
        )}
      </Box>
      <Divider sx={{ mx: 2 }} />
      <List sx={{ flexGrow: 1, py: 1, overflowY: 'auto' }}>
        {items.map((item) => (
          <Tooltip key={item.path} title={mini ? item.text : ''} placement="right">
            <NavButton
              component={RouterLink}
              to={item.path}
              selected={location.pathname === item.path}
              onClick={closeMobile}
              sx={mini ? { justifyContent: 'center', '& .MuiListItemIcon-root': { mr: 0 } } : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              {!mini && <ListItemText primary={item.text} />}
            </NavButton>
          </Tooltip>
        ))}
      </List>
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ p: 1.5 }}>
        <Tooltip title={mini ? t('common.logout') : ''} placement="right">
          <NavButton
            onClick={() => { closeMobile(); logout(); }}
            sx={{
              color: 'error.main',
              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.dark' },
              ...(mini && { justifyContent: 'center', '& .MuiListItemIcon-root': { mr: 0 } }),
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}><LogoutIcon /></ListItemIcon>
            {!mini && <ListItemText primary={t('common.logout')} />}
          </NavButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {isDesktop ? (
        <Drawer
          variant="permanent"
          open
          sx={{
            width: mini ? RAIL_WIDTH : DRAWER_WIDTH, flexShrink: 0,
            transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
            '& .MuiDrawer-paper': {
              width: mini ? RAIL_WIDTH : DRAWER_WIDTH, border: 0, borderRight: `1px solid ${theme.palette.divider}`,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
            },
          }}
        >
          {sidebar}
        </Drawer>
      ) : (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        >
          {sidebar}
        </Drawer>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar
          position="sticky"
          elevation={0}
          color="default"
          sx={{ bgcolor: 'background.paper', color: 'text.primary', borderBottom: `1px solid ${theme.palette.divider}` }}
        >
          <Toolbar sx={{ gap: 0.5 }}>
            <Tooltip title={isDesktop ? (mini ? t('nav.expand_menu', 'Развернуть меню') : t('nav.collapse_menu', 'Свернуть меню')) : ''}>
              <IconButton
                edge="start"
                onClick={isDesktop ? toggleCollapsed : () => setMobileOpen(true)}
                sx={{ mr: 0.5 }}
                aria-label="menu"
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.15rem' }} noWrap>
              {pageTitle}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title={mode === 'dark' ? t('common.light_theme', 'Светлая тема') : t('common.dark_theme', 'Тёмная тема')}>
              <IconButton onClick={toggleMode} color="inherit">
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>
            <NotificationBell />
            <LanguageSwitcher />
            <Tooltip title={user.name || ''}>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5, ml: 0.5 }}>
                <Avatar src={user.avatarUrl} sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.95rem', fontWeight: 700 }}>
                  {user.name?.[0]?.toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>{user.name}</Typography>
                <Typography variant="caption" color="text.secondary">{user.email}</Typography>
              </Box>
              <Divider />
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }}>
                <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>{t('profile.title')}
              </MenuItem>
              <MenuItem onClick={() => { setAnchorEl(null); navigate('/change-password'); }}>
                <ListItemIcon><KeyIcon fontSize="small" /></ListItemIcon>{t('profile.pw_title', 'Сменить пароль')}
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setAnchorEl(null); logout(); }} sx={{ color: 'error.main' }}>
                <ListItemIcon><LogoutIcon fontSize="small" color="error" /></ListItemIcon>{t('common.logout')}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, overflowX: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
